import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Button } from 'antd';
import { DownOutlined, GlobalOutlined } from '@ant-design/icons';
import { languages } from '../../constants/languages';
import { getCountryFlagByLanguage, getCurrentLanguage } from '../../utils/languageHelpers';
import Language from '../../utils/Language';

interface LanguageSelectorProps {
  className?: string;
}

interface FlagData {
  name: string;
  code: string;
  emoji: string;
  image: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '' }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkLanguageChange = () => {
      const newLanguage = getCurrentLanguage();
      if (newLanguage !== currentLanguage) {
        setCurrentLanguage(newLanguage);
      }
    };
    checkLanguageChange();
    intervalRef.current = setInterval(checkLanguageChange, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentLanguage]);

  const handleLanguageSelect = (languageValue: string) => {
    Language.triggerTranslateTo(languageValue);
    setCurrentLanguage(languageValue);
    setIsOpen(false);
  };

  const getCurrentFlag = (): FlagData | null => {
    const flagData = getCountryFlagByLanguage(currentLanguage) as FlagData;
    return flagData || null;
  };

  const getNativeLanguageLabel = (languageValue: string) => {
    const languageObj = languages.find((lang) => lang.value === languageValue);
    return (languageObj as any)?.nativeLabel || languageObj?.label || 'English';
  };

  const filteredLanguages = languages.filter((lang) => {
    const label = lang.label?.toLowerCase() || '';
    const nativeLabel = (lang as any).nativeLabel?.toLowerCase() || '';
    const value = lang.value?.toLowerCase() || '';
    return (
      label.includes(searchQuery.toLowerCase()) ||
      nativeLabel.includes(searchQuery.toLowerCase()) ||
      value.includes(searchQuery.toLowerCase())
    );
  });

  const menuItems = [
    {
      key: 'search-input',
      label: (
        <div className='px-3 py-2 border-b border-gray-200 bg-white sticky top-0 z-10'>
          <input
            type='text'
            placeholder='Search language...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ),
      disabled: true
    },
    ...filteredLanguages.map((language) => {
      const flagData = getCountryFlagByLanguage(language.value) as FlagData;
      return {
        key: language.value,
        label: (
          <div className='flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded transition-colors'>
            {flagData && flagData.image && (
              <img
                src={flagData.image}
                alt={flagData.name}
                className='w-5 h-5 rounded-sm object-cover flex-shrink-0'
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const nextSibling = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextSibling) {
                    nextSibling.style.display = 'inline';
                  }
                }}
              />
            )}
            <span className='text-sm font-medium text-gray-700 truncate'>{language.label}</span>
          </div>
        ),
        onClick: () => handleLanguageSelect(language.value)
      };
    })
  ];

  const currentFlag = getCurrentFlag();

  return (
    <div className={`language-selector ${className}`}>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        open={isOpen}
        onOpenChange={setIsOpen}
        placement='bottomRight'
        overlayClassName='language-dropdown'
        dropdownRender={(menu) => <div style={{ width: '280px' }}>{menu}</div>}>
        <Button
          type='text'
          className='flex items-center gap-2 px-3 py-2 h-auto hover:bg-gray-50 rounded-lg transition-colors'
          style={{ border: 'none', boxShadow: 'none' }}>
          {currentFlag && currentFlag.image ? (
            <img
              src={currentFlag.image}
              alt={currentFlag.name}
              className='w-6 h-6 rounded-sm object-cover'
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const nextSibling = e.currentTarget.nextElementSibling as HTMLElement;
                if (nextSibling) {
                  nextSibling.style.display = 'inline';
                }
              }}
            />
          ) : (
            <GlobalOutlined className='text-gray-500' />
          )}
          <span className='text-sm font-medium text-gray-700 hidden sm:inline'>
            {getNativeLanguageLabel(currentLanguage)}
          </span>
          <DownOutlined className='text-xs text-gray-500 ml-1' />
        </Button>
      </Dropdown>
    </div>
  );
};

export default LanguageSelector;
